import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Monitor, PenTool, ScanLine, Sparkles, Zap } from "lucide-react";
import { EASE } from "./Reveal";

const CTA_URL = "https://manage.scan2ad.com/";

const lineReveal = {
    hidden: { y: "115%" },
    show: (i) => ({
        y: 0,
        transition: { duration: 1, delay: 0.2 + i * 0.14, ease: EASE },
    }),
};

const fade = {
    hidden: { opacity: 0, y: 24 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: 0.7 + i * 0.12, ease: EASE },
    }),
};

const FloatChip = ({ className, delay, children, testid }) => (
    <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay, duration: 0.6, ease: EASE }}
        className={`absolute z-20 ${className}`}
    >
        <div
            data-testid={testid}
            style={{ animation: `float-soft 5s ease-in-out ${delay}s infinite` }}
            className="flex items-center gap-2 rounded-2xl border border-white/60 bg-white/80 px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-xl shadow-slate-900/10 backdrop-blur-md"
        >
            {children}
        </div>
    </motion.div>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const phoneY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

    return (
        <section
            id="home"
            ref={ref}
            className="relative overflow-hidden bg-gradient-to-b from-[#e6faf3] via-[#eefbf7] to-[#eefbf7] pb-20 pt-36 sm:pt-40 lg:pb-28 lg:pt-48"
        >
            <motion.div
                style={{ y: glowY }}
                aria-hidden
                className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-teal-300/30 blur-[120px]"
            />
            <motion.div
                style={{ y: glowY }}
                aria-hidden
                className="pointer-events-none absolute -right-32 top-64 h-[420px] w-[420px] rounded-full bg-amber-300/25 blur-[120px]"
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.35]"
                style={{
                    backgroundImage:
                        "radial-gradient(rgba(13,148,136,0.14) 1px, transparent 1px)",
                    backgroundSize: "26px 26px",
                }}
            />

            <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-8 lg:px-8">
                <div>
                    <motion.div
                        variants={fade}
                        initial="hidden"
                        animate="show"
                        custom={-1}
                        className="mb-7 inline-flex items-center gap-2 rounded-full border border-teal-600/20 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700 shadow-sm backdrop-blur"
                        data-testid="hero-badge"
                    >
                        <Sparkles className="h-3.5 w-3.5" />
                        Advertisements, made effortless
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="font-display text-5xl font-extrabold leading-[1.04] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl"
                    >
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                variants={lineReveal}
                                initial="hidden"
                                animate="show"
                                custom={0}
                            >
                                Create Professional
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span
                                className="block"
                                variants={lineReveal}
                                initial="hidden"
                                animate="show"
                                custom={1}
                            >
                                Ads{" "}
                                <span className="relative inline-block text-teal-600">
                                    in Seconds
                                    <motion.svg
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ delay: 1.15, duration: 0.7, ease: "easeOut" }}
                                        className="absolute -bottom-2 left-0 w-full"
                                        viewBox="0 0 300 14"
                                        fill="none"
                                    >
                                        <motion.path
                                            d="M4 10 C 80 2, 200 2, 296 8"
                                            stroke="#f59e0b"
                                            strokeWidth="6"
                                            strokeLinecap="round"
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: 1 }}
                                            transition={{ delay: 1.15, duration: 0.7, ease: "easeOut" }}
                                        />
                                    </motion.svg>
                                </span>
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        variants={fade}
                        initial="hidden"
                        animate="show"
                        custom={1}
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
                    >
                        Scan a product barcode or upload a photo, add your price and offer,
                        generate a professional ad, and publish it directly to your digital
                        screen.
                    </motion.p>

                    <motion.div
                        variants={fade}
                        initial="hidden"
                        animate="show"
                        custom={2}
                        className="mt-10 flex flex-wrap items-center gap-5"
                    >
                        <motion.a
                            href={CTA_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-create-ad-button"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.96 }}
                            className="group inline-flex items-center gap-2.5 rounded-full bg-teal-600 px-8 py-4 text-base font-semibold text-white shadow-xl shadow-teal-600/30 transition-colors duration-200 hover:bg-teal-700"
                        >
                            Create Your Ad
                            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </motion.a>
                    </motion.div>

                    <motion.div
                        variants={fade}
                        initial="hidden"
                        animate="show"
                        custom={3}
                        className="mt-12 flex flex-wrap gap-x-8 gap-y-4 text-sm font-medium text-slate-600"
                    >
                        {[
                            { icon: PenTool, label: "No design tools needed" },
                            { icon: Zap, label: "Publish in seconds" },
                            { icon: Monitor, label: "Built for digital screens" },
                        ].map((f) => (
                            <span key={f.label} className="inline-flex items-center gap-2.5">
                                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-600/10 ring-1 ring-teal-600/20">
                                    <f.icon className="h-3.5 w-3.5 text-teal-700" />
                                </span>
                                {f.label}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: phoneY }}
                    className="relative mx-auto w-full max-w-[420px] lg:max-w-none"
                >
                    <motion.div
                        initial={{ opacity: 0, y: 60, rotate: 4 }}
                        animate={{ opacity: 1, y: 0, rotate: 0 }}
                        transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
                        className="relative"
                    >
                        <div
                            aria-hidden
                            className="absolute inset-x-8 top-10 bottom-0 rounded-[3rem] bg-teal-500/20 blur-2xl"
                        />
                        <img
                            src="/assets/phone.png"
                            alt="Scan2Ad app Create Ad screen on a phone"
                            data-testid="hero-phone-mockup-image"
                            className="relative z-10 mx-auto w-full max-w-[360px] drop-shadow-2xl"
                        />
                        <FloatChip className="-left-2 top-[33%] sm:-left-10" delay={1.2} testid="hero-chip-scanned">
                            <ScanLine className="h-4 w-4 text-teal-600" />
                            Barcode scanned
                        </FloatChip>
                        <FloatChip className="-right-2 top-[63%] sm:-right-8" delay={1.45} testid="hero-chip-price">
                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 text-xs font-bold text-amber-900">
                                $
                            </span>
                            $2.99 · 2 for $5
                        </FloatChip>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
